import { doc, getDoc, serverTimestamp, updateDoc, Timestamp } from "firebase/firestore/lite";
import { db } from "./firebase";
import { tokensForQuery } from "../../shared/names.mjs";
import type { Invitation, MemberResponse, PlusOne } from "./types";

/**
 * SHA-256 hex. Guest names are never sent to Firestore in the clear during
 * search — only the hash of a token is, as a document id.
 *
 * crypto.subtle needs a secure context, which means https or localhost.
 */
async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function toInvitation(id: string, data: Record<string, unknown>): Invitation {
  const memberNames = (data.memberNames as string[]) ?? [];
  const stored = (data.responses as MemberResponse[]) ?? [];
  return {
    id,
    displayName: (data.displayName as string) || memberNames.join(" & "),
    memberNames,
    plusOneAllowed: Boolean(data.plusOneAllowed),
    // Always hand back one slot per invited person, even if the stored array
    // is short because the invitation has never been answered.
    responses: memberNames.map(
      (_, i) => stored[i] ?? { attending: null, meal: null },
    ),
    plusOne: (data.plusOne as PlusOne | null) ?? null,
    events: (data.events as Record<string, boolean>) ?? {},
    note: (data.note as string) ?? "",
    email: (data.email as string) ?? "",
    respondedAt: data.respondedAt instanceof Timestamp ? data.respondedAt.toDate() : null,
  };
}

async function fetchInvitations(ids: string[]): Promise<Invitation[]> {
  const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, "invitations", id))));
  return snaps
    .filter((snap) => snap.exists())
    .map((snap) => toInvitation(snap.id, snap.data() as Record<string, unknown>));
}

/**
 * Look a guest up by name.
 *
 * Probes hashed tokens most-specific-first and stops at the first hit, so
 * "Priya Raghavan" resolves straight to one invitation while a bare
 * "Raghavan" may legitimately return several for the guest to choose from.
 */
export async function searchGuests(query: string): Promise<Invitation[]> {
  const tokens = tokensForQuery(query);
  if (tokens.length === 0) return [];

  for (const token of tokens) {
    const hash = await sha256Hex(token);
    const snap = await getDoc(doc(db, "lookup", hash));
    if (!snap.exists()) continue;

    const ids = (snap.data().invitationIds as string[]) ?? [];
    const found = await fetchInvitations(ids);
    if (found.length > 0) return found;
  }
  return [];
}

export interface RsvpSubmission {
  responses: MemberResponse[];
  plusOne: PlusOne | null;
  events: Record<string, boolean>;
  note: string;
  email: string;
}

export async function submitRsvp(invitationId: string, reply: RsvpSubmission): Promise<void> {
  await updateDoc(doc(db, "invitations", invitationId), {
    responses: reply.responses,
    plusOne: reply.plusOne,
    events: reply.events,
    note: reply.note,
    email: reply.email,
    respondedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
